// client/src/pages/Register.jsx
import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

export default function Register() {
  const [searchParams] = useSearchParams();
  const referralCode = useMemo(
    () => String(searchParams.get("ref") || "").trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 40),
    [searchParams]
  );
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [verificationSent, setVerificationSent] = useState(null);

  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, "");
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    const username = form.username.trim();
    const email = form.email.trim().toLowerCase();

    if (!username || !email || !form.password) {
      setError("Please fill out username, email, and password.");
      return;
    }

    if (!/^[A-Za-z0-9_.-]{3,24}$/.test(username)) {
      setError(
        "Username must be 3-24 characters and use only letters, numbers, periods, underscores, or hyphens."
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email)) {
      setError("Enter a valid email address.");
      return;
    }

    if (form.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password: form.password, referralCode }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.error === "email_pending_verification") {
          setVerificationSent({ email, username });
          setError(data?.message || "This email is waiting for verification.");
          return;
        }
        throw new Error(data?.message || data?.error || "Failed to create account.");
      }

      if (data?.requiresEmailVerification) {
        setVerificationSent({
          email: data.email || email,
          username: data.username || username,
        });
        return;
      }

      // Backward-compatible fallback in case an older API response is ever returned.
      if (data?.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("ga_token", data.token);
        localStorage.setItem("authToken", data.token);
      }
      if (data?.user) {
        localStorage.setItem("ga_user", JSON.stringify(data.user));
        localStorage.setItem("user", JSON.stringify(data.user));
        if (data.user.email) localStorage.setItem("member_email", data.user.email);
      }
      window.dispatchEvent(new Event("auth:changed"));
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  const resendVerification = async () => {
    const email = verificationSent?.email || form.email.trim().toLowerCase();
    if (!email) return;

    setResending(true);
    setError("");
    setNotice("");

    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Could not resend verification email.");
      }
      setNotice(data?.message || "A new verification link was sent.");
    } catch (err) {
      setError(err?.message || "Could not resend verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="ga-auth-wrap">
      <style>{`
        .ga-auth-wrap{
          min-height: calc(100vh - 140px);
          display:flex;
          align-items:center;
          justify-content:center;
          padding:32px 16px;
        }
        .ga-auth-card{
          width:100%; max-width:720px; border-radius:18px;
          background:rgba(0,0,0,.70); border:1px solid rgba(255,0,0,.25);
          box-shadow:0 18px 60px rgba(0,0,0,.55); position:relative; overflow:hidden;
        }
        .ga-auth-card:before{
          content:""; position:absolute; inset:0;
          background:radial-gradient(900px 420px at 20% 0%,rgba(255,0,0,.14),transparent 55%),radial-gradient(700px 420px at 90% 30%,rgba(255,70,70,.10),transparent 60%);
          pointer-events:none;
        }
        .ga-auth-inner{position:relative;padding:22px 22px 18px;backdrop-filter:blur(8px)}
        .ga-auth-chip{display:inline-flex;align-items:center;gap:10px;font-size:12px;color:rgba(255,255,255,.90);padding:8px 12px;border-radius:999px;background:rgba(0,0,0,.45);border:1px solid rgba(255,0,0,.30);box-shadow:0 10px 30px rgba(0,0,0,.35);margin-bottom:14px}
        .ga-auth-dot{width:10px;height:10px;border-radius:50%;background:#ff1a1a;box-shadow:0 0 0 3px rgba(255,0,0,.18),0 0 18px rgba(255,0,0,.55)}
        .ga-auth-title{font-family:Georgia,"Times New Roman",serif;margin:0 0 6px;font-size:36px;line-height:1.05;color:rgba(255,255,255,.96);text-shadow:0 10px 30px rgba(0,0,0,.55)}
        .ga-auth-sub{margin:0 0 18px;color:rgba(255,255,255,.78);font-size:14px;line-height:1.55;max-width:60ch}
        .ga-auth-error,.ga-auth-notice{margin:0 0 14px;padding:10px 12px;border-radius:12px;color:rgba(255,255,255,.92)}
        .ga-auth-error{background:rgba(255,0,0,.10);border:1px solid rgba(255,0,0,.35)}
        .ga-auth-notice{background:rgba(35,180,90,.12);border:1px solid rgba(35,180,90,.35)}
        .ga-auth-form{display:flex;flex-direction:column;gap:12px}
        .ga-grid{display:grid;gap:12px;grid-template-columns:1fr}
        @media(min-width:720px){.ga-grid{grid-template-columns:1fr 1fr}}
        .ga-field label{display:block;font-size:12px;color:rgba(255,255,255,.82);margin:0 0 6px}
        .ga-field small{display:block;margin-top:6px;color:rgba(255,255,255,.58);font-size:11px;line-height:1.4}
        .ga-input{width:100%;height:44px;border-radius:12px;padding:0 12px;background:rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.18);color:rgba(255,255,255,.92);caret-color:rgba(255,255,255,.92);font-size:16px;line-height:44px;outline:none;-webkit-text-fill-color:currentColor}
        .ga-input::placeholder{color:rgba(255,255,255,.55)}
        .ga-input:focus{border-color:rgba(255,0,0,.55);box-shadow:0 0 0 4px rgba(255,0,0,.16)}
        .ga-pw-row{display:flex;gap:10px;align-items:center}.ga-pw-row .ga-input{flex:1}
        .ga-pw-toggle{height:44px;padding:0 12px;border-radius:12px;background:rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.14);color:rgba(255,255,255,.85);cursor:pointer;white-space:nowrap}
        .ga-primary,.ga-secondary{height:46px;border-radius:14px;color:white;cursor:pointer;font-weight:700;letter-spacing:.2px;margin-top:4px;padding:0 18px}
        .ga-primary{border:1px solid rgba(255,0,0,.35);background:linear-gradient(135deg,rgba(255,0,0,.95),rgba(190,0,0,.95));box-shadow:0 14px 34px rgba(255,0,0,.18),0 18px 60px rgba(0,0,0,.45)}
        .ga-secondary{border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.07)}
        .ga-primary:disabled,.ga-secondary:disabled{opacity:.65;cursor:not-allowed}
        .ga-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:8px}
        .ga-auth-footer{margin-top:14px;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;color:rgba(255,255,255,.75);font-size:13px}
        .ga-link{color:rgba(255,120,120,.95);text-decoration:none}.ga-link:hover{text-decoration:underline;color:rgba(255,170,170,.98)}
        .ga-divider{margin-top:18px;height:1px;background:linear-gradient(90deg,transparent,rgba(255,0,0,.35),transparent)}
        .ga-email-box{margin:14px 0;padding:14px;border-radius:14px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);word-break:break-word}
        .ga-referral-banner{margin:0 0 14px;padding:11px 13px;border-radius:13px;background:rgba(255,184,42,.11);border:1px solid rgba(255,184,42,.35);color:#ffe0a3;font-weight:800}
      `}</style>

      <div className="ga-auth-card">
        <div className="ga-auth-inner">
          <div className="ga-auth-chip">
            <span className="ga-auth-dot" />
            <span>GAStreaming.live • New Member</span>
          </div>

          {verificationSent ? (
            <>
              <h1 className="ga-auth-title">Check your email</h1>
              <p className="ga-auth-sub">
                We sent a verification link to finish creating the account for <strong>{verificationSent.username}</strong>.
                The account is not active until that email is verified.
              </p>
              <div className="ga-email-box">{verificationSent.email}</div>
              {error ? <div className="ga-auth-error">{error}</div> : null}
              {notice ? <div className="ga-auth-notice">{notice}</div> : null}
              <div className="ga-actions">
                <button className="ga-primary" type="button" onClick={resendVerification} disabled={resending}>
                  {resending ? "Sending..." : "Resend verification email"}
                </button>
                <button
                  className="ga-secondary"
                  type="button"
                  onClick={() => {
                    setVerificationSent(null);
                    setError("");
                    setNotice("");
                  }}
                >
                  Use a different email
                </button>
              </div>
              <div className="ga-auth-footer">
                <span>Already verified? <Link className="ga-link" to="/login">Sign in</Link></span>
                <Link className="ga-link" to="/">Back home</Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="ga-auth-title">Create account</h1>
              <p className="ga-auth-sub">
                One account unlocks chat, uploads, profiles, the community feed, and tournament registration. Email verification is required.
              </p>
              {referralCode ? (
                <div className="ga-referral-banner">Admin referral applied: {referralCode}. The reward remains pending until your verified account passes founder review.</div>
              ) : null}

              {error ? <div className="ga-auth-error">{error}</div> : null}

              <form className="ga-auth-form" onSubmit={handleSubmit}>
                <div className="ga-grid">
                  <div className="ga-field">
                    <label>Username</label>
                    <input
                      className="ga-input"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      autoComplete="username"
                      autoCapitalize="none"
                      spellCheck={false}
                      maxLength={24}
                      placeholder="HooperKing23"
                      required
                    />
                    <small>Usernames are unique and checked case-insensitively.</small>
                  </div>

                  <div className="ga-field">
                    <label>Email</label>
                    <input
                      className="ga-input"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                      autoCapitalize="none"
                      spellCheck={false}
                      placeholder="you@example.com"
                      required
                    />
                    <small>You must click the verification link sent to this address.</small>
                  </div>
                </div>

                <div className="ga-grid">
                  <div className="ga-field">
                    <label>Password</label>
                    <div className="ga-pw-row">
                      <input
                        className="ga-input"
                        name="password"
                        type={showPass ? "text" : "password"}
                        value={form.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        placeholder="••••••••"
                        required
                      />
                      <button type="button" className="ga-pw-toggle" onClick={() => setShowPass((v) => !v)}>
                        {showPass ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  <div className="ga-field">
                    <label>Confirm</label>
                    <div className="ga-pw-row">
                      <input
                        className="ga-input"
                        name="confirmPassword"
                        type={showPass2 ? "text" : "password"}
                        value={form.confirmPassword}
                        onChange={handleChange}
                        autoComplete="new-password"
                        placeholder="••••••••"
                        required
                      />
                      <button type="button" className="ga-pw-toggle" onClick={() => setShowPass2((v) => !v)}>
                        {showPass2 ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>
                </div>

                <button disabled={submitting} className="ga-primary" type="submit">
                  {submitting ? "Sending verification..." : "Create account"}
                </button>

                <div className="ga-auth-footer">
                  <span>Already have an account? <Link className="ga-link" to="/signin">Sign in</Link></span>
                  <Link className="ga-link" to="/">Back home</Link>
                </div>
                <div className="ga-divider" />
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
